/**
 * Traduce el evento WS `file_rag_status` (canal de CHAT, #274) al patch que
 * `updateAttachmentRagStatus` aplica sobre la caché de mensajes.
 *
 * Vive fuera de `Chat.jsx` para que sea verificable: el handler que lo llama está dentro
 * de un `useEffect` de un componente de ~2.000 líneas, y probar la traducción a través de
 * él exigiría montar medio chat. Aquí es una función pura con sus asserts (#1270).
 */

/** Payload del evento tal como lo emite `ragNotifier.fileChatStatus`. */
export interface FileRagStatusEvent {
  fileId?: string;
  threadId?: string;
  status?: string;
  error?: string | null;
  warningCount?: number;
  lastWarning?: string | null;
}

export interface RagStatusPatch {
  ragStatus?: string | null;
  ragError?: string | null;
  warningCount?: number | null;
  lastWarning?: string | null;
}

export function buildRagStatusPatch(event: FileRagStatusEvent): RagStatusPatch {
  const { status, error, warningCount, lastWarning } = event;

  const patch: RagStatusPatch = {
    ragStatus: status,
    ragError: error ?? null,
    // El badge solo existe en 'completed'; el emisor ya pone 0 en el resto de estados.
    warningCount: Number.isFinite(warningCount) ? (warningCount as number) : 0,
  };

  // #1270 — el motivo del aviso. La key se añade SOLO si el evento la trae, y la distinción
  // importa durante un deploy: el emisor la manda SIEMPRE (null incluido, para poder limpiar
  // un aviso anterior), así que «ausente» solo puede significar un backend previo al fix. Ahí
  // escribir null borraría el motivo que el fetch inicial sí trajo, cambiando un texto
  // correcto por el genérico «Procesado con avisos». Ausente ⇒ no se toca el campo.
  //
  // `|| null` y no `?? null`: una cadena vacía es «sin aviso», y en BD ese caso es NULL. El
  // patch se aplica con spread sobre lo que trajo el fetch, así que los dos shapes tienen
  // que coincidir o el render distinguiría de dónde vino el dato.
  if (lastWarning !== undefined) patch.lastWarning = lastWarning || null;

  return patch;
}
